/**
 * PartnersChart — live horizontal bar chart of top N countries by
 * unique partner-university count. Data source: /api/analytics/countries-by-partners
 * (which reads the same partnerships collection as /api/homepage/stats — no
 * hardcoded values here, no decorative placeholders).
 *
 * Bars animate their width from 0% → real-share% when the chart scrolls
 * into view. Uses IntersectionObserver, matches the site's existing motion
 * language (transition-timing-function ease-out, staggered).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { BarChart3, ArrowRight } from "lucide-react";

export default function PartnersChart({ limit = 8 }) {
  const [rows, setRows] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [lit, setLit] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get(`/analytics/countries-by-partners?limit=${limit}`)
      .then((r) => {
        setRows(r.data.rows || []);
        setUpdatedAt(r.data.dataset_updated_at || null);
      })
      .catch(() => setRows([]));
  }, [limit]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setLit(true); io.disconnect(); }
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [rows]);

  const total  = rows ? rows.reduce((s, r) => s + r.partner_count, 0) : 0;
  const max    = rows && rows.length ? rows[0].partner_count : 1;

  return (
    <div
      ref={ref}
      className="border border-ink/12 rounded-3xl bg-paper p-6 md:p-10"
      data-testid="partners-chart"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.2em] text-elec font-bold">
            <BarChart3 size={13} strokeWidth={2.4} /> Live from our partnerships dataset
          </div>
          <h3 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight leading-tight text-ink">
            Partner universities by country
          </h3>
          <p className="mt-1 text-sm text-muted">
            Top {rows?.length ?? limit} host countries. {total || "…"} unique universities among them.
          </p>
        </div>
        <Link
          to="/partnerships"
          data-testid="partners-chart-see-all"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-ink hover:text-elec whitespace-nowrap"
        >
          See all <ArrowRight size={13} strokeWidth={2.4} />
        </Link>
      </div>

      <div className="mt-8 space-y-3.5" data-testid="partners-chart-rows">
        {(rows || Array.from({ length: limit })).map((row, i) => {
          const pct = row ? Math.max(6, Math.round((row.partner_count / max) * 100)) : 40;
          return (
            <div
              key={row?.country || i}
              data-testid={row ? `partners-chart-row-${row.country}` : undefined}
              className="grid grid-cols-[minmax(96px,132px)_1fr_auto] items-center gap-3 md:gap-4"
            >
              <div className="text-[13px] font-bold text-ink truncate">
                {row ? row.country : <span className="moe-shimmer inline-block w-24 h-4 rounded" />}
              </div>
              <div className="relative h-8 rounded-lg bg-ink/[0.05] overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-lg"
                  style={{
                    width: lit && row ? `${pct}%` : "0%",
                    background: "linear-gradient(90deg, var(--moe-elec) 0%, var(--moe-elec) 88%, rgba(65,80,255,0.75) 100%)",
                    transition: `width 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 90}ms`,
                  }}
                />
              </div>
              <div className="text-[13px] font-bold tabular-nums text-ink w-8 text-right">
                {row ? row.partner_count : ""}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-muted italic">
          Computed live from the ministry_of_exchange_eu_schengen_master dataset.
        </div>
        {updatedAt && (
          <div
            className="text-[11px] text-muted inline-flex items-center gap-1.5"
            data-testid="partners-chart-updated-at"
            title={updatedAt}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-elec animate-pulse" />
            Last updated <span className="font-semibold text-ink">{relTime(updatedAt)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function relTime(iso) {
  try {
    const then = new Date(iso).getTime();
    if (!then || isNaN(then)) return "recently";
    const diffMs = Date.now() - then;
    const s = Math.max(0, Math.floor(diffMs / 1000));
    if (s < 60)   return "just now";
    const m = Math.floor(s / 60);
    if (m < 60)   return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24)   return `${h} ${h === 1 ? "hour" : "hours"} ago`;
    const d = Math.floor(h / 24);
    if (d < 30)   return `${d} ${d === 1 ? "day" : "days"} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12)  return `${mo} ${mo === 1 ? "month" : "months"} ago`;
    return `${Math.floor(mo / 12)}y ago`;
  } catch { return "recently"; }
}
